/**
 * useMonthlyMerchantInvoices
 * Mirrors the mobile hook of the same name.
 * Manages monthly merchant fee invoices (Option B / Option C billing).
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/shared/services/api';

export interface MonthlyMerchantInvoice {
  id: string;
  merchant_id: string;
  billing_month: string;
  total_fees_cents: number;
  platform_fees_cents: number;
  merchant_fees_cents: number;
  carried_over_amount_cents: number;
  carried_over_from_month: string | null;
  invoice_count: number;
  status: 'pending' | 'processing' | 'paid';
  due_date: string;
  paid_at: string | null;
  fwd_payment_intent_id: string | null;
  fwd_payment_id: string | null;
  payment_reference: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface MonthlyInvoiceItem {
  id: string;
  invoice_number: string;
  amount_cents: number;
  customer_name: string | null;
  fee_tier: 'hybrid' | 'absorbed';
  technology_fee_cents: number;
  merchant_fee_cents: number;
  status: string;
  paid_at: string | null;
  refunded_at: string | null;
}

export function useMonthlyMerchantInvoices() {
  const [monthlyInvoices, setMonthlyInvoices] = useState<MonthlyMerchantInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMonthlyInvoices = useCallback(async (isRefreshing = false) => {
    try {
      if (isRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Regenerate monthly invoice first (fire-and-forget), then fetch updated list
      await apiClient.post('/payments/generate-monthly-invoice', {}).catch(() => {});

      const response = await apiClient.get<MonthlyMerchantInvoice[]>('/payments/merchant-monthly-invoices');
      setMonthlyInvoices(response || []);
    } catch (e: any) {
      console.error('[useMonthlyMerchantInvoices] Error:', e);
      setError(e?.message || 'Failed to load monthly invoices');
      setMonthlyInvoices([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchMonthlyInvoices(true);
  }, [fetchMonthlyInvoices]);

  const createPaymentUrl = useCallback(async (monthlyInvoiceId: string) => {
    const response = await apiClient.post<{
      ok: boolean;
      payment_url: string;
      client_secret: string;
      payment_intent_id: string;
    }>('/payments/create-monthly-payment-url', { monthly_invoice_id: monthlyInvoiceId });
    if (!response.ok) throw new Error('Failed to create payment URL');
    return response;
  }, []);

  const fetchInvoiceItems = useCallback(async (billingMonth: string): Promise<MonthlyInvoiceItem[]> => {
    try {
      const response = await apiClient.get<MonthlyInvoiceItem[]>(
        `/payments/merchant-monthly-invoice-items?billing_month=${billingMonth}`
      );
      return response || [];
    } catch (e) {
      console.error('[useMonthlyMerchantInvoices] fetchInvoiceItems error:', e);
      return [];
    }
  }, []);

  useEffect(() => {
    fetchMonthlyInvoices(false);
  }, []);

  const totalPendingCents = monthlyInvoices
    .filter(inv => inv.status === 'pending')
    .reduce((sum, inv) => sum + inv.total_fees_cents, 0);

  const pendingInvoice = monthlyInvoices.find(inv => inv.status === 'pending');

  return {
    monthlyInvoices,
    loading,
    refreshing,
    error,
    refresh,
    createPaymentUrl,
    pendingInvoice,
    totalPendingCents,
    fetchInvoiceItems,
  };
}
